import { nanoid } from 'nanoid'
import * as Sentry from '@sentry/react'
import { CaselawStorageField } from '@/common/types/caselaw-identifiers'
import { matterPaths } from '@/routing/paths'

export type CaselawViewerContext = {
  conversationId?: string | null
  messageId?: string | null
  matterId?: string | null
}

/**
 * Builds the URL for the standalone caselaw reference viewer.
 *
 * Routes to appropriate URL pattern based on which storage field exists:
 * - s3_dir → /caselaw/s3_dir/{path} (modern/Assistant)
 * - s3_path → /caselaw/s3_path/{path} (legacy features)
 *
 * @param metadata - Reference metadata containing s3_dir or s3_path
 * @param context - Optional query parameters for analytics context
 * @returns Standalone viewer URL with optional query params, or empty string if no valid identifier
 */
export function buildCaselawStandaloneViewerURL(metadata: Record<string, unknown>, context?: CaselawViewerContext): string {
  const matterId = typeof context?.matterId === 'string' && context.matterId.trim().length > 0 ? context.matterId.trim() : null

  let basePath: string | null = null

  const s3Dir = metadata[CaselawStorageField.S3_DIR]
  const s3Path = metadata[CaselawStorageField.S3_PATH]

  if (typeof s3Dir === 'string' && s3Dir) {
    basePath = matterPaths.referenceViewer.caselaw(matterId, CaselawStorageField.S3_DIR, encodeURIComponent(s3Dir))
  } else if (typeof s3Path === 'string' && s3Path) {
    basePath = matterPaths.referenceViewer.caselaw(matterId, CaselawStorageField.S3_PATH, encodeURIComponent(s3Path))
  }

  if (!basePath) {
    const sentry_transaction_id = nanoid()
    Sentry.withScope((scope) => {
      scope.setTags({ transaction_id: sentry_transaction_id })
      Sentry.captureException(new Error(`No valid s3_dir or s3_path found to build standalone viewer URL`), {
        extra: {
          onLine: navigator.onLine,
          cookieEnabled: navigator.cookieEnabled,
          metadata: JSON.stringify(metadata),
        },
      })
    })
    return ''
  }

  const searchParams = new URLSearchParams()

  if (typeof context?.conversationId === 'string' && context.conversationId.trim().length > 0) {
    searchParams.set('conversationId', context.conversationId.trim())
  }
  if (typeof context?.messageId === 'string' && context.messageId.trim().length > 0) {
    searchParams.set('messageId', context.messageId.trim())
  }
  // matterId is now encoded in the path for matter-scoped URLs, not as a query param

  const queryString = searchParams.toString()
  return queryString.length > 0 ? `${basePath}?${queryString}` : basePath
}
